import { z } from 'zod';
import { UserSummary } from './profile.js';
export const MarriageStatus = z.enum([
    'draft',
    'awaiting_spouse',
    'in_review',
    'approved',
    'rejected',
    'withdrawn',
]);
export const MarriageAudience = z.enum(['only_me', 'family', 'public']);
export const MarriageDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((v) => {
    const date = new Date(`${v}T12:00:00Z`);
    return (Number.isFinite(+date) &&
        date.toISOString().slice(0, 10) === v &&
        Number(v.slice(0, 4)) >= 1900);
}, 'Choose a valid wedding date');
export const MarriageTimezone = z
    .string()
    .max(100)
    .refine((v) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: v });
        return true;
    }
    catch {
        return false;
    }
}, 'Choose a valid time zone');
export const MarriageVerificationView = z.object({
    id: z.string(),
    name: z.string(),
    mime: z.enum(['application/pdf', 'image/jpeg', 'image/png']),
    size: z.number().int().positive(),
});
export const MarriageMilestoneView = z.object({
    id: z.string(),
    version: z.number().int().nonnegative(),
    status: MarriageStatus,
    spouse: UserSummary.nullable(),
    weddingDate: MarriageDate.nullable(),
    timezone: MarriageTimezone,
    audience: MarriageAudience,
    verification: MarriageVerificationView.nullable(),
    needsMyConfirmation: z.boolean(),
    canEdit: z.boolean(),
    rejectionReason: z.string().nullable(),
    years: z.number().int().nonnegative().nullable(),
    stars: z.number().int().min(0).max(5),
});
export const SaveMarriageMilestoneInput = z.object({
    spouseId: z.string().min(1).max(128),
    expectedVersion: z.number().int().nonnegative().nullable(),
    weddingDate: MarriageDate.nullable(),
    timezone: MarriageTimezone,
    audience: MarriageAudience,
    verificationId: z.string().max(128).nullable(),
    mode: z.enum(['draft', 'submit']),
});
export const MarriageVersionInput = z.object({ expectedVersion: z.number().int().nonnegative() });
export const ReviewMarriageInput = MarriageVersionInput.extend({
    decision: z.enum(['approve', 'reject']),
    reason: z.string().trim().min(10).max(1000),
});
export const MarriageReviewView = MarriageMilestoneView.extend({
    participants: z.array(UserSummary),
    verificationAvailable: z.boolean(),
});
export const MarriageVerificationDownload = MarriageVerificationView.extend({ base64: z.string() });
export const MarriagePublicView = z.object({
    years: z.number().int().nonnegative(),
    stars: z.number().int().min(0).max(5),
});
export const MARRIAGE_VERIFICATION_MAX_BYTES = 5 * 1024 * 1024;
export function marriageYears(weddingDate, timezone, now = new Date()) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        calendar: 'iso8601',
        numberingSystem: 'latn',
    })
        .formatToParts(now)
        .map((p) => [p.type, p.value]));
    const today = `${parts.year}-${parts.month}-${parts.day}`;
    if (!MarriageDate.safeParse(weddingDate).success || weddingDate > today)
        throw new Error('Wedding date must be today or earlier');
    return (Number(parts.year) -
        Number(weddingDate.slice(0, 4)) -
        (today.slice(5) < weddingDate.slice(5) ? 1 : 0));
}
/** 6–9 years can enter awards but carry no milestone badge (confirmed product rule). */
export function marriageStars(years) {
    return years >= 25 ? 5 : years >= 20 ? 4 : years >= 15 ? 3 : years >= 10 ? 2 : years <= 5 ? 1 : 0;
}
//# sourceMappingURL=marriage.js.map